import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const SystemConfig = () => {
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState({
    system_name: 'CISTenant',
    system_email: '',
    system_phone: '',
    timezone: 'Africa/Nairobi',
    currency: 'KES',
    date_format: 'DD/MM/YYYY',
    invoice_prefix: 'REN',
    bill_prefix: 'BIL',
    receipt_prefix: 'RCP',
    auto_send_invoices: true,
    auto_send_receipts: true,
    late_payment_penalty_percent: '0',
    invoice_due_days: '7',
    enable_notifications: true,
    enable_sms: true,
    enable_email: true,
    enable_penalties: true,
    water_rate_per_m3: '150',
    paybill_number: '',
    paybill_account: '',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/api/config/system');
      if (response.data) {
        setConfigData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch system config:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setConfigData({
      ...configData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/api/config/system', configData);
      toast.success('System configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save system configuration');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <SettingsIcon color="primary" />
        <Typography variant="h6">System Configuration</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* General Settings */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              General Settings
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="System Name"
              name="system_name"
              value={configData.system_name}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="System Email"
              name="system_email"
              type="email"
              value={configData.system_email}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="System Phone"
              name="system_phone"
              value={configData.system_phone}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                name="timezone"
                value={configData.timezone}
                onChange={handleChange}
                label="Timezone"
              >
                <MenuItem value="Africa/Nairobi">East Africa Time (EAT)</MenuItem>
                <MenuItem value="Africa/Lagos">West Africa Time (WAT)</MenuItem>
                <MenuItem value="Africa/Cairo">Eastern European Time (EET)</MenuItem>
                <MenuItem value="Africa/Johannesburg">South Africa Time (SAST)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                name="currency"
                value={configData.currency}
                onChange={handleChange}
                label="Currency"
              >
                <MenuItem value="KES">Kenyan Shilling (KES)</MenuItem>
                <MenuItem value="USD">US Dollar (USD)</MenuItem>
                <MenuItem value="EUR">Euro (EUR)</MenuItem>
                <MenuItem value="GBP">British Pound (GBP)</MenuItem>
                <MenuItem value="TZS">Tanzanian Shilling (TZS)</MenuItem>
                <MenuItem value="UGX">Ugandan Shilling (UGX)</MenuItem>
                <MenuItem value="ZAR">South African Rand (ZAR)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Date Format</InputLabel>
              <Select
                name="date_format"
                value={configData.date_format}
                onChange={handleChange}
                label="Date Format"
              >
                <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Invoice & Payment Settings */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Invoice & Payment Settings
            </Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Invoice Prefix"
              name="invoice_prefix"
              value={configData.invoice_prefix}
              onChange={handleChange}
              helperText="e.g., REN-251203-5847"
              inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Bill Prefix"
              name="bill_prefix"
              value={configData.bill_prefix}
              onChange={handleChange}
              helperText="e.g., BIL-251203-9234"
              inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Receipt Prefix"
              name="receipt_prefix"
              value={configData.receipt_prefix}
              onChange={handleChange}
              helperText="e.g., RCP-251203-8765"
              inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="M-Pesa Paybill / Till Number"
              name="paybill_number"
              value={configData.paybill_number || ''}
              onChange={handleChange}
              placeholder="e.g. 522533"
              helperText="M-Pesa Paybill or Till number shown in payment SMS"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Invoice Due Days"
              name="invoice_due_days"
              type="number"
              value={configData.invoice_due_days}
              onChange={handleChange}
              helperText="Default days until invoice is due"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Late Payment Penalty (%)"
              name="late_payment_penalty_percent"
              type="number"
              value={configData.late_payment_penalty_percent}
              onChange={handleChange}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              helperText="Penalty percentage for late payments"
              disabled={!configData.enable_penalties}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Water Rate per m³ (KES)"
              name="water_rate_per_m3"
              type="number"
              value={configData.water_rate_per_m3}
              onChange={handleChange}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Rate per cubic meter for water billing"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={configData.enable_penalties}
                  onChange={handleChange}
                  name="enable_penalties"
                />
              }
              label="Enable Penalty System"
            />
          </Grid>

          {/* Notification Settings */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Notification Settings
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={configData.enable_notifications}
                  onChange={handleChange}
                  name="enable_notifications"
                />
              }
              label="Enable System Notifications"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={configData.enable_email}
                  onChange={handleChange}
                  name="enable_email"
                />
              }
              label="Enable Email Notifications"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={configData.enable_sms}
                  onChange={handleChange}
                  name="enable_sms"
                />
              }
              label="Enable SMS Notifications"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={configData.auto_send_invoices}
                  onChange={handleChange}
                  name="auto_send_invoices"
                />
              }
              label="Auto-send Invoices"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={configData.auto_send_receipts}
                  onChange={handleChange}
                  name="auto_send_receipts"
                />
              }
              label="Auto-send Receipts"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              Save Configuration
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default SystemConfig;
